import { useRef, useState, useEffect } from "react";
import { X, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { resolveImg } from "@/lib/api";
import { uploadProductImage } from "@/lib/supabase";

export const ImageUploader = ({ images, onChange, onUploadingChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { onUploadingChange?.(uploading); }, [uploading, onUploadingChange]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const publicUrl = await uploadProductImage(file);
        uploaded.push(publicUrl);
      }
      onChange([...images, ...uploaded]);
      toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(err?.message || "Image upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => onChange(images.filter((_, i) => i !== idx));

  return (
    <div data-testid="image-uploader">
      <div className="flex flex-wrap gap-3">
        {images.map((img, i) => (
          <div key={i} className="relative h-24 w-24 overflow-hidden border border-border bg-secondary">
            <img src={resolveImg(img)} alt="" className="h-full w-full object-cover" />
            <button
              type="button"
              data-testid={`remove-image-${i}`}
              onClick={() => remove(i)}
              className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center bg-white/90 text-stone-700 hover:bg-wine hover:text-white"
            >
              <X size={12} />
            </button>
          </div>
        ))}
        <button
          type="button"
          data-testid="upload-trigger"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex h-24 w-24 flex-col items-center justify-center gap-1 border border-dashed border-input text-stone-400 hover:border-wine hover:text-wine"
        >
          {uploading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
          <span className="text-[9px] uppercase tracking-[0.15em]">{uploading ? "Uploading" : "Add"}</span>
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={handleFiles} data-testid="file-input" />
    </div>
  );
};
